<template>
  <iColumn
    :width="getOptionValue(LayoutWidth)"
    :height="getOptionValue(LayoutHeight)"
    :min-height="getOptionValue(MinHeight)"
    :min-width="getOptionValue(MinWidth)"
    :max-height="getOptionValue(MaxHeight)"
    :max-width="getOptionValue(MaxWidth)"
    :gap="formGap"
    :style-overrides="formStyleOverrides"
  >
    <iTextInput
      :model-value="loggedInFirstName || firstName"
      :focus="true"
      :style-overrides="inputStyleOverrides"
      placeholder="First Name"
      width="fill"
      name="lastname"
      type="text"
      @update:model-value="firstName=$event"
    />
    <iTextInput
      :model-value="loggedInFirstName || lastName"
      :style-overrides="inputStyleOverrides"
      placeholder="Last Name"
      width="fill"
      name="lastname"
      type="text"
      @update:model-value="lastName=$event"
    />
    <iTextInput
      :model-value="loggedInEmail || email"
      :style-overrides="inputStyleOverrides"
      class="contact-form-input"
      name="email"
      placeholder="Email address*"
      type="email"
      width="fill"
      right-icon="pencil"
      :disabled="!!loggedInEmail"
      @update:model-value="handleEmailChange"
    />
    <iTextArea
      v-model="message"
      :style-overrides="textAreaStyleOverrides"
      placeholder="Hi there..."
      :rows="4"
      width="fill"
    />
    <iButton
      :is-loading="loadingCreateSupportConversation"
      :disabled="!isValidForm"
      :style-overrides="buttonStyleOverrides"
      loading-spinner-position="inside"
      variant="primary"
      @click="submitMessage"
    >
      Submit
    </iButton>
  </iColumn>
</template>

<script>
// `%/apps/conversations/stores/conversation-store` and
// `%/components/community/chatrooms/slack-style/Chatroom.vue` both
// live outside the editor directory tree. Canvas preview renders the
// form fields locally without trying to wire up the contact-us
// conversation flow.
import { ContactUsPageElementMixin } from "%/dynamic-component-editor/standard-pages/contact-us-page/contact-us-page-element-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import FontColor from "%/dynamic-component-editor/options/font-color";
import IconColor from "%/dynamic-component-editor/options/icon-color";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import LayoutGap from "%/dynamic-component-editor/options/layout-gap";
import Padding, { PADDING_DEFAULT_EMPTY_VALUE } from "%/dynamic-component-editor/options/padding";
import Margins, { MARGIN_DEFAULT_EMPTY_VALUE } from "%/dynamic-component-editor/options/margins";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import FormBackgroundColor from "%/dynamic-component-editor/standard-pages/contact-us-page/form-background-color";
import InputBackgroundColor from "%/dynamic-component-editor/standard-pages/contact-us-page/input-background-color";

export default {
  name: "ContactForm",
  mixins: [
    ContactUsPageElementMixin,
  ],
  elementPanelSettings: {
    displayName: "Contact Me",
    displayIcon: "comment",
    elementGroup: "contactUsElements",
    elementType: "element",
  },
  optionBundles: [
    LayoutWidthHeightOptions,
  ],
  options: [
    new FontColor({
      "default": {
        "value": "standard",
        "source": "theme.font",
      },
    }),
    new IconColor({
      "default": {
        "value": "standard",
        "source": "theme.font",
      },
    }),
    new BorderColor({
      "default": {
        "value": "none",
        "source": "theme.border",
      },
    }),
    new BackgroundColor({
      "default": {
        "value": "standard",
        "source": "theme.background",
      },
    }),
    new FormBackgroundColor({
      "default": {
        "value": "none",
        "source": "theme.background",
      },
    }),
    new InputBackgroundColor({
      "default": {
        "value": "subtle",
        "source": "theme.background",
      },
    }),
    new BorderRadius({
      "default": "large",
    }),
    new LayoutGap({ "default": "standard" }, false, true, "Spacing Between Inputs"),
    new Padding({
      "default": { top: null, right: null, bottom: null, left: null },
    }),
    new Margins({
      "default": { top: null, right: null, bottom: null, left: null },
    }),
  ],
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
      LayoutWidth,
      LayoutHeight,
      MinHeight,
      MaxHeight,
      MinWidth,
      MaxWidth,
      BorderColor,
      BorderRadius,
      FontColor,
      IconColor,
      BackgroundColor,
      FormBackgroundColor,
      InputBackgroundColor,
      LayoutGap,
      Padding,
      Margins,
      BaseOption,
    };
  },
  computed: {
    isValidForm() {
      return Boolean(this.firstName && this.lastName && this.email && this.message);
    },
    fontColor() {
      return this.getOptionValue(FontColor) || "standard";
    },
    iconColor() {
      return this.getOptionValue(IconColor) || "standard";
    },
    buttonBackgroundColor() {
      return this.getOptionValue(BackgroundColor) || "standard";
    },
    formBackgroundColor() {
      return this.getOptionValue(FormBackgroundColor);
    },
    inputBackgroundColor() {
      return this.getOptionValue(InputBackgroundColor);
    },
    formGap() {
      return this.getOptionValue(LayoutGap);
    },
    formPadding() {
      const paddingValues = { ...this.getOptionValue(Padding, BaseOption.$states.DEFAULT, null) };
      Object.keys(paddingValues).forEach(side => {
        if (paddingValues[side] === null) {
          paddingValues[side] = PADDING_DEFAULT_EMPTY_VALUE;
        }
      });
      return paddingValues;
    },
    formMargins() {
      const marginValues = { ...this.getOptionValue(Margins, BaseOption.$states.DEFAULT, null) };
      Object.keys(marginValues).forEach(side => {
        if (marginValues[side] === null) {
          marginValues[side] = MARGIN_DEFAULT_EMPTY_VALUE;
        }
      });
      return marginValues;
    },
    formStyleOverrides() {
      return {
        backgroundColor: this.formBackgroundColor,
        paddingTop: this.formPadding.top,
        paddingRight: this.formPadding.right,
        paddingBottom: this.formPadding.bottom,
        paddingLeft: this.formPadding.left,
        marginTop: this.formMargins.top,
        marginRight: this.formMargins.right,
        marginBottom: this.formMargins.bottom,
        marginLeft: this.formMargins.left,
      };
    },
    inputBorderColor() {
      return this.getOptionValue(BorderColor) || "none";
    },
    inputBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    inputStyleOverrides() {
      return {
        inputFontColor: this.fontColor,
        inputBackgroundColor: this.inputBackgroundColor,
        inputHoverBackgroundColor: this.inputBackgroundColor,
        disabledInputBackgroundColor: this.inputBackgroundColor,
        inputBorderColor: this.inputBorderColor,
        inputBorderRadius: this.inputBorderRadius,
        inputBorderFocusColor: this.inputBorderColor,
        inputBorderFocusRadius: this.inputBorderRadius,
        disabledInputFontColor: this.fontColor,
        disabledInputBorderColor: this.inputBorderColor,
      };
    },
    textAreaStyleOverrides() {
      // iTextArea has no inputHoverBackgroundColor in its style guide — omit it.
      const overrides = { ...this.inputStyleOverrides };
      delete overrides.inputHoverBackgroundColor;
      return overrides;
    },
    buttonStyleOverrides() {
      return {
        primaryVariantBackgroundColor: this.buttonBackgroundColor,
        primaryVariantHoverBackgroundColor: this.buttonBackgroundColor,
        primaryVariantDisabledBackgroundColor: this.buttonBackgroundColor,
        primaryVariantFontColor: this.fontColor,
        primaryVariantHoverFontColor: this.fontColor,
        primaryVariantDisabledFontColor: this.fontColor,
      };
    },
  },
  methods: {
    submitMessage() {
      this.createSupportConversation(
        this.myFirstName,
        this.myLastName,
        this.myEmail,
        this.message,
        () => {
          this.statusMessage = "Thanks for reaching out — we'll be in touch.";
        },
        () => {
          this.errorMessage = "Couldn't send your message. Please try again.";
        },
      );
    },
  },
};
</script>

<style scoped lang="scss">
.error-message{
  color: v-bind("$getStyles.errorMessageFontColor");
}
.contact-form-input :deep(.icon-right .i-icon),
.contact-form-input :deep(.icon-right .i-icon svg) {
  color: v-bind("iconColor") !important;
  fill: v-bind("iconColor") !important;
}
</style>
